import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, Send, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Markdown } from "@/components/agyin/Markdown";
import { stream } from "@/components/agyin/AgyinPanel";

const SUGGEST = {
  platform_admin: ["Summarise the platform today", "Which tickets have breached SLA?", "Which tenants are past due?", "Open the ticket queue"],
  platform_ops: ["Summarise open tickets", "Which tickets have breached SLA?", "Which tickets are unassigned?", "Open the ticket queue"],
};
const AREAS = { tenants: "Tenants", invoices: "Subscription invoices", plans: "Plans and add-ons", internal: "Internal team", tickets: "the ticket queue", settings: "Settings and logs" };
const areaName = (path) => {
  const [, root, seg = "", id] = path.split("/");
  const a = !seg ? (root === "ops" ? "the Ops console" : "the platform overview") : AREAS[seg] || seg;
  return id ? `${a}, on a record page` : a;
};
const newSid = () => `a-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

function Msg({ m, last, busy, onClose }) {
  const navigate = useNavigate();
  const a = m.action;
  if (m.role === "user") return <div className="ml-8 whitespace-pre-wrap rounded-lg bg-[#13294B] px-3 py-2 text-sm text-white" data-testid="admin-agyin-msg-user">{m.content}</div>;
  return (
    <div className={`mr-4 rounded-lg border px-3 py-2 text-sm ${m.error ? "po-tone-danger" : "bg-white text-slate-800"}`} style={{ borderColor: "var(--po-border)" }} data-testid="admin-agyin-msg-assistant">
      {m.content ? <Markdown text={m.content.replace(/```agyin[\s\S]*$/, "").trim()} /> : <div>{busy && last ? "Thinking…" : ""}</div>}
      {a?.action === "navigate" && <Button size="sm" data-testid="admin-agyin-navigate-button" className="mt-2 bg-[#13294B] hover:bg-[#1c3a66]"
        onClick={async () => { onClose(); await api.post("/agyin/admin/log", { action: "navigate_executed", route: a.route }).catch(() => {}); navigate(a.route); }}>
        {a.label} <ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>}
    </div>
  );
}

export function AdminAgyinPanel() {
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(() => sessionStorage.getItem("agyin_admin_sid") || newSid());
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const ctrl = useRef(null);
  useEffect(() => { sessionStorage.setItem("agyin_admin_sid", sid); }, [sid]);
  useEffect(() => { if (open && !msgs.length) api.get(`/agyin/admin/sessions/${sid}`).then((r) => setMsgs(r.data)).catch(() => {}); }, [open, sid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  useEffect(() => () => ctrl.current?.abort(), []);
  const patchLast = (f) => setMsgs((m) => { const n = [...m]; n[n.length - 1] = f(n[n.length - 1]); return n; });
  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    ctrl.current?.abort();
    ctrl.current = new AbortController();
    try {
      const done = await stream({ session_id: sid, message, context: { route: loc.pathname } }, (c) => patchLast((l) => ({ ...l, content: l.content + c })), ctrl.current.signal, "/api/agyin/admin/chat");
      patchLast((l) => ({ role: "assistant", content: done?.text ?? l.content, action: done?.action }));
    } catch (e) {
      if (e.name !== "AbortError") patchLast(() => ({ role: "assistant", content: e.message, error: true }));
    } finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid="admin-agyin-help-button" onClick={() => setOpen(true)} aria-label="Open Agyin"
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-[#13294B] px-4 py-2.5 text-sm font-medium text-white shadow-[var(--po-shadow-raise)] transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#45BFB4] print:hidden">
        <Sparkles className="h-4 w-4 text-[#45BFB4]" /><span className="hidden sm:inline">Agyin</span></button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:w-[420px] sm:max-w-none lg:w-[33vw] lg:min-w-[400px] lg:max-w-[560px]" data-testid="admin-agyin-panel">
          <SheetHeader className="border-b px-4 py-3 text-left" style={{ borderColor: "var(--po-border)" }}>
            <SheetTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#2B7F77]" />Agyin for admins</SheetTitle>
            <SheetDescription className="text-xs" data-testid="admin-agyin-page-context">You're in {areaName(loc.pathname)}. Ask me to find a tenant or ticket, or to sum things up.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="admin-agyin-messages">
            {!msgs.length && <div className="space-y-2"><p className="text-sm text-slate-600">Hi {user?.name?.split(" ")[0]}, how can I help?</p>
              {(SUGGEST[user?.role] || []).map((s) => <button key={s} onClick={() => send(s)} data-testid={`admin-agyin-suggest-${s.slice(0, 12).replace(/\W/g, "")}`} className="block w-full rounded-lg border px-3 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50" style={{ borderColor: "var(--po-border)" }}>{s}</button>)}</div>}
            {msgs.map((m, i) => <Msg key={i} m={m} last={i === msgs.length - 1} busy={busy} onClose={() => setOpen(false)} />)}
            <div ref={end} />
          </div>
          <div className="border-t p-3" style={{ borderColor: "var(--po-border)" }}>
            <div className="flex items-end gap-2">
              <Textarea data-testid="admin-agyin-input" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask Agyin about tenants, tickets or plans…"
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} className="min-h-[44px] resize-none" />
              <Button data-testid="admin-agyin-send-button" disabled={busy || !text.trim()} onClick={() => send()} className="bg-[#13294B] hover:bg-[#1c3a66]"><Send className="h-4 w-4" /></Button>
            </div>
            <button data-testid="admin-agyin-new-chat" onClick={() => { setSid(newSid()); setMsgs([]); }} className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600"><X className="h-3 w-3" />New chat</button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
